import React, { useMemo, useState } from "react";
import { FiBell, FiCalendar, FiMail, FiMessageCircle, FiStar, FiUsers } from "react-icons/fi";
import SettingsHeader from "./SettingsHeader";

const MailIcon = FiMail;
const CalendarIcon = FiCalendar;
const MessageIcon = FiMessageCircle;
const UsersIcon = FiUsers;
const StarIcon = FiStar;
const BellIcon = FiBell;

const NotificationRow = ({ icon: Icon, title, subtitle, checked, onChange }) => (
  <div className="pp-settings-toggle-row">
    <span className="pp-settings-toggle-copy">
      <span className="pp-settings-toggle-title">
        <span className="pp-settings-toggle-icon" aria-hidden="true">
          <Icon />
        </span>
        {title}
      </span>
      <span className="pp-settings-toggle-subtitle">{subtitle}</span>
    </span>
    <label className="pp-settings-switch">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span aria-hidden="true" />
      <em>{checked ? "On" : "Off"}</em>
    </label>
  </div>
);

export default function NotificationsSettingsScreen({ t, onBack, form = {}, onChange, onSave }) {
  const initialParentActions = useMemo(() => !form.newsletterOptOut, [form.newsletterOptOut]);
  const [values, setValues] = useState({
    newsletter: true,
    agenda: true,
    messages: false,
    parentActions: initialParentActions,
    activities: true,
  });
  const [saving, setSaving] = useState(false);

  const updateValue = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));

    if (key === "parentActions" && onChange) {
      onChange({
        target: {
          name: "newsletterOptOut",
          type: "checkbox",
          checked: !value,
        },
      });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pp-settings-screen pp-settings-subscreen pp-settings-notifications-screen">
      <SettingsHeader
        t={t}
        onBack={onBack}
      />

      <section className="pp-settings-card pp-settings-toggle-card">
        <div className="pp-settings-card-heading">
          <span className="pp-settings-card-icon" aria-hidden="true">
            <BellIcon />
          </span>
          <div>
            <h3>{t("parentPortal.settings.notification_preferences", { defaultValue: "Notification preferences" })}</h3>
            <p>{t("parentPortal.settings.notification_preferences_intro", { defaultValue: "Choose the topics you want email notifications for." })}</p>
          </div>
        </div>

        <NotificationRow
          icon={MailIcon}
          title={t("parentPortal.settings.newsletter", { defaultValue: "Newsletter" })}
          subtitle={t("parentPortal.settings.newsletter_subtitle", { defaultValue: "Receive the newsletter" })}
          checked={values.newsletter}
          onChange={(value) => updateValue("newsletter", value)}
        />
        <NotificationRow
          icon={CalendarIcon}
          title={t("parentPortal.settings.agenda", { defaultValue: "Agenda" })}
          subtitle={t("parentPortal.settings.agenda_subtitle", { defaultValue: "Reminders and updates" })}
          checked={values.agenda}
          onChange={(value) => updateValue("agenda", value)}
        />
        <NotificationRow
          icon={MessageIcon}
          title={t("parentPortal.settings.new_messages", { defaultValue: "New messages" })}
          subtitle={t("parentPortal.settings.new_messages_subtitle", { defaultValue: "Messages from school or teachers" })}
          checked={values.messages}
          onChange={(value) => updateValue("messages", value)}
        />
        <NotificationRow
          icon={UsersIcon}
          title={t("parentPortal.settings.parent_actions", { defaultValue: "Parent actions" })}
          subtitle={t("parentPortal.settings.parent_actions_subtitle", { defaultValue: "Updates about parent actions" })}
          checked={values.parentActions}
          onChange={(value) => updateValue("parentActions", value)}
        />
        <NotificationRow
          icon={StarIcon}
          title={t("parentPortal.settings.activities", { defaultValue: "Activities and events" })}
          subtitle={t("parentPortal.settings.activities_subtitle", { defaultValue: "Updates about activities" })}
          checked={values.activities}
          onChange={(value) => updateValue("activities", value)}
        />

        <div className="pp-settings-actions">
          <button type="button" className="pp-settings-primary-button" onClick={save} disabled={saving}>
            {saving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save", { defaultValue: "Save" })}
          </button>
        </div>
      </section>
    </div>
  );
}
